import React, { useState, useEffect } from 'react';
import { apiClient, ENDPOINTS } from '../services/apiConfig';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  Card,
  CardContent,
  Badge,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Help as HelpIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  EmojiEvents as EmojiEventsIcon,
  Timeline as TimelineIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Define the Achievement interface
interface Achievement {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  date?: string;
}


// Add this interface near the top of the file with other interfaces
interface MoodEntry {
  _id: string;
  date: string;
  primaryEmotion: string;
  subEmotion: string;
  score: number;
  factors: string[];
  notes: string;
  creativeActivity?: {
    completed: boolean;
    duration: number;
    type: string;
  };
}

const Profile = () => {
  const { currentUser, logout, updateUser } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingMoodEntries, setLoadingMoodEntries] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [moodEntries, setMoodEntries] = useState<MoodEntry[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  
  // User profile state

  // Update the profile state to match userModel fields
const [profile, setProfile] = useState({
  name: currentUser?.username || '',
  email: currentUser?.email || '',
  phone: currentUser?.phone || '',
  bio: currentUser?.bio || 'I am on a journey to better understand and manage my emotions.',
  profilePicture: currentUser?.profilePicture || '',
  createdAt: currentUser?.createdAt || '',
  updatedAt: currentUser?.updatedAt || ''
});  
  
  // User stats state
  const [stats, setStats] = useState({
    daysActive: 0,
    moodEntries: 0,
    modulesCompleted: 0,
    journalEntries: 0,
    averageMood: 0,
    streakDays: 0,
  });
  
  // User achievements state with proper typing
  const [achievements, setAchievements] = useState<Achievement[]>([]);
 const navigate = useNavigate();
  // Settings state
const [settings, setSettings] = useState({
  emailNotifications: true,
  moodReminders: true,
  weeklyReports: true,
  darkMode: false,
  dataSharing: false,
  twoFactorAuth: false,
  // Add createdAt and updatedAt if you want to display them
  createdAt: '',
  updatedAt: ''
});
  
  // Fetch user data from backend
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setLoadingMoodEntries(true);
        
        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        if (!token) {
          // If no token, try to use currentUser data if available
          if (currentUser) {
            setProfile({
              name: currentUser.username || '',
              createdAt: currentUser.createdAt || '',
              updatedAt: currentUser.updatedAt || '',
              email: currentUser.email || '',
              phone: currentUser.phone || '',
              bio: currentUser.bio || 'I am on a journey to better understand and manage my emotions.',
              profilePicture: currentUser.profilePicture || '',
            });
            setLoading(false);
          } else {
            setError('Authentication token not found');
            setLoading(false);
          }
          return;
        }
        
        // Configure headers with token
        const config = {
          headers: {
            Authorization: `Bearer ${token}`
          }
        };
        
        // Fetch user profile
        try {
          //const profileResponse = await axios.get('http://localhost:5000/api/users/profile', config);
          //const profileResponse = await apiClient.get('/api/users/profile', config);
          const profileResponse = await apiClient.get(ENDPOINTS.AUTH.PROFILE, config);
          if (profileResponse.data) {
            setProfile({
              name: profileResponse.data.username || '',
              email: profileResponse.data.email || '',
              createdAt: profileResponse.data.createdAt || '',
              updatedAt: profileResponse.data.updatedAt || '',
              phone: profileResponse.data.phone || '',
              bio: profileResponse.data.bio || 'I am on a journey to better understand and manage my emotions.',
              profilePicture: profileResponse.data.profilePicture || '',
            });
          }
        } catch (profileErr) {
          console.error('Error fetching profile:', profileErr);
          // If profile fetch fails, use currentUser data
          if (currentUser) {
            setProfile({
              name: currentUser.username || '',
              createdAt: currentUser.createdAt || '',
              updatedAt: currentUser.updatedAt || '',
              email: currentUser.email || '',
              phone: currentUser.phone || '',
              bio: currentUser.bio || 'I am on a journey to better understand and manage my emotions.',
              profilePicture: currentUser.profilePicture || '',
            });
          }
        }
        
        // Fetch user stats
        setLoadingStats(true);
        setStatsError(false);
        try {
          //const statsResponse = await axios.get('http://localhost:5000/api/users/stats', config);
          //const statsResponse = await apiClient.get('/api/users/stats', config);
          const statsResponse = await apiClient.get(ENDPOINTS.AUTH.STATS, config);
          if (statsResponse.data) {
            setStats(statsResponse.data);
              
            setStats({
              daysActive: statsResponse.data.daysActive || 0,
              moodEntries: statsResponse.data.moodEntries || 0,
              modulesCompleted: statsResponse.data.modulesCompleted || 0,
              journalEntries: statsResponse.data.journalEntries || 0,
              averageMood: statsResponse.data.averageMood || 0,
              streakDays: statsResponse.data.streakDays || 0,
            });
          }
        } catch (statsErr) {
          console.error('Error fetching user stats:', statsErr);
          setStatsError(true);
          setSnackbarMessage('Failed to load user statistics');
          setSnackbarOpen(true);
        } finally {
          setLoadingStats(false);
        }
        
        // Fetch user achievements
        try {
          //const achievementsResponse = await axios.get('http://localhost:5000/api/users/achievements', config);
          //const achievementsResponse = await apiClient.get('/api/users/achievements', config);
          const achievementsResponse = await apiClient.get(ENDPOINTS.AUTH.ACHIEVEMENTS, config);
          if (achievementsResponse.data) {
            setAchievements(achievementsResponse.data);
          }
        } catch (achievementsErr) {
          console.error('Error fetching achievements:', achievementsErr);
          setSnackbarMessage('Failed to load achievements');
          setSnackbarOpen(true);
        }
        
        // Fetch user settings
try {
  //const settingsResponse = await axios.get('http://localhost:5000/api/users/settings', config);
  const settingsResponse = await apiClient.get(ENDPOINTS.AUTH.SETTINGS, config);
  
  if (settingsResponse.data) {
    setSettings({
      emailNotifications: settingsResponse.data.emailNotifications || true,
      moodReminders: settingsResponse.data.moodReminders ?? true,
      createdAt: settingsResponse.data.createdAt || '',
      updatedAt: settingsResponse.data.updatedAt || '',
      weeklyReports: settingsResponse.data.weeklyReports || true,
      darkMode: settingsResponse.data.darkMode || false,
      dataSharing: settingsResponse.data.dataSharing || false,
      twoFactorAuth: settingsResponse.data.twoFactorAuth || false,
    });
  }
} catch (settingsErr) {
  console.error('Error fetching settings:', settingsErr);
  setSnackbarMessage('Failed to load user settings');
  setSnackbarOpen(true);
}
        
        // Fetch mood entries
        try {
          //const moodEntriesResponse = await axios.get('http://localhost:5000/api/moods', config);
          //const moodEntriesResponse = await apiClient.get('/api/moods', config);
          const moodEntriesResponse = await apiClient.get(ENDPOINTS.MOODS, config);
          if (moodEntriesResponse.data) {
            setMoodEntries(moodEntriesResponse.data);
          }
        } catch (moodErr) {
          console.error('Error fetching mood entries:', moodErr);
        } finally {
          setLoadingMoodEntries(false);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data');      

        
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser]); // Add currentUser as dependency

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target.files && e.target.files[0]) {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('profilePicture', file);
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      // Configure headers with token
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
         
        }
      };
      
      //const response = await axios.post('http://localhost:5000/api/users/upload-profile-picture', formData, config);
      //const response = await apiClient.post('/api/users/upload-profile-picture', formData, config);
      const response = await apiClient.post(ENDPOINTS.AUTH.UPLOAD_PROFILE_PICTURE, formData, config);
      
      if (response.data) {
        // Update profile picture in state
        setProfile({
          ...profile,
          profilePicture: response.data.profilePicture
        });
        
        // Update user in context
        updateUser({
          ...currentUser,
          profilePicture: response.data.profilePicture
        });
        
        setSnackbarMessage('Profile picture updated successfully!');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setSnackbarMessage('Failed to upload profile picture');
      setSnackbarOpen(true);
    }
  }
};

  const handleSaveProfile = async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      // Configure headers with token
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Prepare data to send
      const profileData = {
        username: profile.name,
        email: profile.email,
        phone: profile.phone,
        bio: profile.bio,
      };
      
      // Update profile
      //const response = await axios.put('http://localhost:5000/api/users/profile', profileData, config);
      //const response = await apiClient.put(ENDPOINTS.AUTH.PROFILE, profileData, config);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await apiClient.put(ENDPOINTS.AUTH.PROFILE, profileData, config);
      
      if (response.data) {
        // Update local storage with new user data
        const updatedUser = {
          ...currentUser,
          username: response.data.username,
          email: response.data.email,
          phone: response.data.phone,
          bio: response.data.bio,
        };
        
        // Update user in context
        updateUser(updatedUser);
        
        setEditMode(false);
        setSnackbarMessage('Profile updated successfully!');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setSnackbarMessage('Failed to update profile');
      setSnackbarOpen(true);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  const handleEditProfile = () => {
    setEditMode(true);
  };
  
  // Update handleCancelEdit to reset to fetched values
  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset to fetched values
    setProfile({
      name: profile.name,
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt,
      email: profile.email,
      phone: profile.phone,
      bio: profile.bio,
      profilePicture: profile.profilePicture,
    });
  };


  
  // Update handleSettingChange to save to backend
  const handleSettingChange = async (setting: keyof typeof settings) => {
    try {
      // Update local state
      const newSettings = {
        ...settings,
        [setting]: !settings[setting],
      };
      
      setSettings(newSettings);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      // Configure headers with token
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Update setting on backend
      //await axios.put('http://localhost:5000/api/users/settings', { 
      //const response = await apiClient.put(ENDPOINTS.AUTH.SETTINGS, { 
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await apiClient.put(ENDPOINTS.AUTH.SETTINGS, { 
      [setting]: newSettings[setting] 
      }, config);
      
      setSnackbarMessage(`Setting updated: ${setting}`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating setting:', err);
      setSnackbarMessage('Failed to update setting');
      setSnackbarOpen(true);
    }
  };
  
  // Add these states for password change
const [showPasswordDialog, setShowPasswordDialog] = useState(false);
const [currentPassword, setCurrentPassword] = useState('');
const [newPassword, setNewPassword] = useState('');
const [confirmPassword, setConfirmPassword] = useState('');
const [passwordError, setPasswordError] = useState('');

// Add this function to handle password change
const handlePasswordChange = async () => {
  // Reset error
  setPasswordError('');
  
  // Validate passwords
  if (newPassword !== confirmPassword) {
    setPasswordError('New passwords do not match');
    return;
  }
  
  if (newPassword.length < 6) {
    setPasswordError('Password must be at least 6 characters');
    return;
  }
  
  try {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      setError('Authentication token not found');
      return;
    }
    
    // Configure headers with token
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    
    // Send password change request
    //const response = await axios.put(
    await apiClient.put('/api/auth/change-password',
      {
        currentPassword,
        newPassword
      },
      config
    );
    
    // Close dialog and show success message
    setShowPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSnackbarMessage('Password changed successfully!');
    setSnackbarOpen(true);
  } catch (err) {
    console.error('Error changing password:', err);
    setPasswordError('Failed to change password. Please check your current password.');
  }
};
  const handleProfileChange = (field: keyof typeof profile, value: string) => {
    setProfile({
      ...profile,
      [field]: value,
    });
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  const handleLogout = () => {
    logout();
  };

  const faqData = [
    {
      question: "How does the Mood Friend help?",
      answer: "Mood Friend is an AI-powered companion that helps you understand your emotions better. It analyzes your mood entries, provides personalized insights, and offers supportive conversations to help you navigate your emotional well-being journey. It's available 24/7 and adapts to your unique emotional patterns over time."
    },
    {
      question: "Is my data private and secure?",
      answer: "Yes, your data privacy and security are our top priorities. All your personal information and mood entries are encrypted and stored securely. We never share your data with third parties without your explicit consent. You can review our privacy policy for more details on how we protect your information."
    },
    {
      question: "How can I get the most out of MoodCraft?",
      answer: "To maximize your MoodCraft experience, try to log your moods regularly, engage with Mood Friend the AI companion for insights, explore the creative outlet features, and participate in self-help modules. The more consistently you use the app, the more personalized and helpful it becomes. We recommend setting daily reminders and taking a few minutes each day to reflect on your emotions."
    },
    {
      question: "Can I export my mood data?",
      answer: "Yes, you can export your mood data from the Settings tab. This feature allows you to download your mood entries, insights, and patterns as a CSV or PDF file. This can be useful for sharing with healthcare providers or for your own personal records and reflection."
    }
  ];

  return (
    <>
     
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Profile Summary */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    editMode ? (
                      <IconButton 
  sx={{ bgcolor: 'primary.main', color: 'white', width: 30, height: 30 }}
  component="label"
>
  <CloudUploadIcon sx={{ fontSize: 16 }} />
  <input
    type="file"
    hidden
    accept="image/*"
    onChange={handleProfilePictureUpload}
  />
</IconButton>
                    ) : null
                  }
                >
                  <Avatar
                    sx={{ width: { xs: 72, sm: 100 }, height: { xs: 72, sm: 100 }, mb: 2 }}
                    alt={profile.name}
                    src={profile.profilePicture || undefined}
                  >
                    {profile.name.charAt(0)}
                  </Avatar>
                </Badge>
                
                {editMode ? (
                  <TextField
                    fullWidth
                    label="Name"
                    value={profile.name}
                    onChange={(e) => handleProfileChange('name', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                ) : (
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontFamily: '"Lato", sans-serif', fontSize: { xs: 20, sm: 24 } }}
                  >
                    {profile.name}
                  </Typography>
                )}
                
                {editMode ? (
                  <TextField
                    fullWidth
                    label="Bio"
                    multiline
                    rows={3}
                    value={profile.bio}
                    onChange={(e) => handleProfileChange('bio', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                ) : (
                  <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2, fontFamily: '"Quicksand", sans-serif' }}>
                    {profile.bio}
                  </Typography>
                )}
                
                {editMode ? (
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <Button 
                      variant="contained" 
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                      fullWidth
                    >
                      Save
                    </Button>
                    <Button 
                      variant="outlined" 
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                      fullWidth
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button 
                    variant="outlined" 
                    startIcon={<EditIcon />}
                    onClick={handleEditProfile}
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Lato", sans-serif' }}>
                Contact Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Email" secondary={profile.email} />
                </ListItem>
                {editMode ? (
                  <ListItem>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={profile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                    />
                  </ListItem>
                ) : (
                  <ListItem>
                    <ListItemText primary="Phone" secondary={profile.phone} />
                  </ListItem>
                )}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Button 
                variant="outlined" 
                color="error" 
                fullWidth
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Paper>
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 2, md: 3 } }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ mb: 3 }}
              >
                <Tab label="Stats & Achievements" />
                <Tab label="Settings" />
                <Tab label="Help & Support" />
              </Tabs>
              
              {/* Stats & Achievements Tab */}
              {currentTab === 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Lato", sans-serif' }}>
                    Stats
                  </Typography>
                  {loadingStats ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : statsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Failed to load stats. Please try again later.
                    </Alert>
                  ) : (
                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 3, md: 4 } }}>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }}>
                            {stats.daysActive}
                          </Typography>
                          <Typography variant="body2">Days Active</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }}>
                            {stats.moodEntries}
                          </Typography>
                          <Typography variant="body2">Mood Entries</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }}>
                            {stats.modulesCompleted}
                          </Typography>
                          <Typography variant="body2">Modules Completed</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }}>
                            {stats.journalEntries}
                          </Typography>
                          <Typography variant="body2">Journal Entries</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }}>
                            {stats.averageMood}/10
                          </Typography>
                          <Typography variant="body2">Average Mood</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }}>
                            {stats.streakDays}
                          </Typography>
                          <Typography variant="body2">Day Streak</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  )}
                  {/* Add this section for Mood Entries */}

                  
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Lato", sans-serif' }}>
                    Achievements
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {achievements.map((achievement) => (
                      <Grid item xs={12} sm={6} key={achievement.id}>
                        <Card 
                          variant="outlined"
                          sx={{ 
                            opacity: achievement?.completed ? 1 : 0.6,
                            bgcolor: achievement.completed ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <EmojiEventsIcon 
                                color={achievement.completed ? 'primary' : 'disabled'} 
                                sx={{ mr: 1 }}
                              />
                              <Typography variant="h6" sx={{ fontFamily: '"Lato", sans-serif' }}>
                                {achievement.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                              {achievement.description}
                            </Typography>
                            {achievement.completed && achievement.date && (
                              <Typography variant="caption" display="block" sx={{ mt: 1, fontFamily: '"Quicksand", sans-serif' }}>
                                Achieved on {achievement.date}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
              
              {/* Settings Tab */}
              {currentTab === 1 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Notifications
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <NotificationsIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email Notifications" 
                        secondary="Receive important updates via email" 
                      />
                      <Switch
                        edge="end"
                        checked={settings.emailNotifications}
                        onChange={() => handleSettingChange('emailNotifications')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <NotificationsIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Mood Reminders" 
                        secondary="Get reminders to log your mood" 
                      />
                      <Switch
                        edge="end"
                        checked={settings.moodReminders}
                        onChange={() => handleSettingChange('moodReminders')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TimelineIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Weekly Reports" 
                        secondary="Receive weekly summaries of your mood and progress" 
                      />
                      <Switch
                        edge="end"
                        checked={settings.weeklyReports}
                        onChange={() => handleSettingChange('weeklyReports')}
                      />
                    </ListItem>
                  </List>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Appearance
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <PaletteIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Dark Mode" 
                        secondary="Use dark theme throughout the app" 
                      />
                      <Switch
                        edge="end"
                        checked={settings.darkMode}
                        onChange={() => handleSettingChange('darkMode')}
                      />
                    </ListItem>
                  </List>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Privacy & Security
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <SecurityIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Two-Factor Authentication" 
                        secondary="Add an extra layer of security to your account" 
                      />
                      <Switch
                        edge="end"
                        checked={settings.twoFactorAuth}
                        onChange={() => handleSettingChange('twoFactorAuth')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PsychologyIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Anonymous Data Sharing" 
                        secondary="Help improve our AI by sharing anonymized data" 
                      />
                      <Switch
                        edge="end"
                        checked={settings.dataSharing}
                        onChange={() => handleSettingChange('dataSharing')}
                      />
                    </ListItem>
                  </List>
                  
                  <Box sx={{ mt: 3 }}>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      onClick={() => setShowPasswordDialog(true)}
                    >
                      Change Password
                    </Button>
                  </Box>
                </>
              )}
              
              {/* Help & Support Tab */}
              {currentTab === 2 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Help & Support
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Need immediate emotional support? Please contact a Crisis helpline or mental health professional.
                  </Alert>
                  
                  <Typography variant="subtitle1" gutterBottom>
                    Frequently Asked Questions
                  </Typography>
                  <List>
                    {faqData.map((faq, index) => (
                      <React.Fragment key={index}>
                        <ListItem 
                          button 
                          onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        >
                          <ListItemIcon>
                            <HelpIcon />
                          </ListItemIcon>
                          <ListItemText primary={faq.question} />
                        </ListItem>
                        {expandedFaq === index && (
                          <Box sx={{ pl: 6, pr: 2, pb: 2, pt: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {faq.answer}
                            </Typography>
                          </Box>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" gutterBottom>
                    Contact Support
                  </Typography>
                  
                    <Button variant="contained" color="primary" onClick={() => navigate('/contact-us')}                      
                    >
                      Contact Us
                    </Button>   
                  

                </>
              )}
            </Paper>
          </Grid>
        </Grid>
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
        />
        
        {/* Password Change Dialog */}
        <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)}>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Current Password"
              type="password"
              fullWidth
              variant="outlined"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              margin="dense"
              label="New Password"
              type="password"
              fullWidth
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Confirm New Password"
              type="password"
              fullWidth
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 1 }}
            />
            {passwordError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {passwordError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handlePasswordChange} variant="contained">
              Change Password
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default Profile;
